import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, Globe, Zap, Code2, CheckCircle2, XCircle, ExternalLink, Calendar,
} from "lucide-react";
import dayjs from "dayjs";

const Row = ({ label, children, testId }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    <span className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400 pt-0.5">{label}</span>
    <div className="flex-1 min-w-0 text-sm text-gray-800" data-testid={testId}>{children}</div>
  </div>
);

export default function CrawlerInfo({ code, open, onOpenChange }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSource, setShowSource] = useState(false);

  useEffect(() => {
    if (!open || !code) return;
    setLoading(true);
    setInfo(null);
    setShowSource(false);
    api.get(`/crawlers/${code}/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [open, code]);

  const isPw = info?.strategy === "playwright";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="crawler-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Globe className="h-5 w-5 text-[#0F4C81]" />
            Crawler-Regeln
          </DialogTitle>
          <DialogDescription className="sr-only">Quelle, Fetch-Strategie und Scraping-Regeln dieses Crawlers.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !info ? (
          <div className="py-10 text-center text-sm text-gray-500">Informationen konnten nicht geladen werden.</div>
        ) : (
          <div className="mt-1">
            <Row label="Blatt" testId="info-blatt">
              <span className="font-medium">{info.blatt_name}</span>
              <span className="block text-xs text-gray-400 font-mono">{info.code} · {info.bundesland}</span>
            </Row>

            <Row label="Strategie" testId="info-strategy">
              {info.has_adapter ? (
                <span className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-semibold border ${isPw ? "bg-violet-50 text-violet-800 border-violet-300" : "bg-sky-50 text-sky-800 border-sky-300"}`}>
                  {isPw ? <Zap className="h-3.5 w-3.5" /> : <Code2 className="h-3.5 w-3.5" />}
                  {isPw ? "Playwright (JS-Rendering)" : "HTTP (statisches HTML)"}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-semibold border bg-gray-100 text-gray-500 border-gray-300">
                  <XCircle className="h-3.5 w-3.5" /> Kein Adapter
                </span>
              )}
            </Row>

            <Row label="Adapter" testId="info-adapter">
              {info.has_adapter ? (
                <span className="inline-flex items-center gap-1.5 text-green-700"><CheckCircle2 className="h-4 w-4" /> Dediziert portiert</span>
              ) : (
                <span className="text-gray-500">Nicht implementiert – wird nicht automatisch geladen.</span>
              )}
            </Row>

            <Row label="Jahr-Filter" testId="info-year">
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-gray-400" /> Nur Hefte {info.year_filter}</span>
            </Row>

            <Row label="Quelle" testId="info-source">
              <a href={info.source_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[#0F4C81] hover:underline break-all">
                {info.source_url} <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            </Row>

            {info.description && (
              <Row label="Scraping-Regeln" testId="info-rules">
                <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-gray-600">{info.description}</pre>
              </Row>
            )}

            {info.source && (
              <div className="pt-2">
                <button
                  onClick={() => setShowSource((s) => !s)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#0F4C81] hover:underline"
                  data-testid="info-toggle-source"
                >
                  <Code2 className="h-3.5 w-3.5" />
                  {showSource ? "Quelltext ausblenden" : "Adapter-Quelltext anzeigen (Beleg)"}
                </button>
                {showSource && (
                  <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-gray-900 text-gray-100 text-[11px] leading-relaxed p-3 font-mono" data-testid="info-source-code">{info.source}</pre>
                )}
              </div>
            )}

            <Row label="Statistik" testId="info-stats">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                <span><b>{info.doc_count}</b> Dokumente</span>
                <span><b>{info.seen_count}</b> bekannte URLs</span>
                <span>letzter Lauf: {info.last_run_at ? dayjs(info.last_run_at).format("DD.MM.YY HH:mm") : "—"}</span>
                <span>zuletzt neu: <b>{info.last_new_count}</b></span>
              </div>
            </Row>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
